import React from 'react'
import Link from 'next/link'

/**
 * Onward links rendered in the body of both 404 pages.
 *
 * Shared between `pages/404.tsx` and `app/not-found.tsx` because those two are
 * required to render identical content and there is no way to enforce that by
 * hand — the App Router one is what actually answers unmatched URLs while both
 * routers coexist, so a change made to only the pages copy is invisible in
 * production.
 *
 * These are here for agents as much as for people. Every nonexistent path on
 * the site resolves here, now with a real HTTP 404 status (see the
 * `fallback: 'blocking'` note in `pages/[hash]/index.tsx`). A 404 whose body
 * only says "not found" tells a crawler nothing about where to go next; naming
 * the machine-readable entry points means an agent that lands on a dead URL can
 * recover in one hop. They survive into the `Accept: text/markdown` rendering
 * of the page as an ordinary markdown link list.
 */

interface RecoveryLink {
  href: string
  label: string
  /** Static file under `public/`, so it needs a plain <a>: routing it through
   *  the client router would 404 it. */
  static?: boolean
}

export const RECOVERY_LINKS: RecoveryLink[] = [
  // The homepage IS the search surface (it renders `DupeInputHomepage`), so it
  // gets the search label. `/app` is `pages/app.tsx` — the "Apps & Extensions"
  // download page, NOT a search interface — so it must not be labelled as one:
  // an agent following a "search for dupes" link to a download page has been
  // sent to the wrong product surface. (`llms.txt` had the same mislabel and is
  // corrected too.) `/chats/prompt` was considered and rejected: at the time it
  // resolved through the `chats/[...parts]` catch-all behind the
  // `chats-unified-route` / `chats-v2-brain` flags (both since retired and
  // deleted in PostHog), and a recovery link must not depend on a flag.
  { href: '/', label: 'Search for dupes' },
  { href: '/app', label: 'Get the app' },
  { href: '/products/furniture', label: 'Browse furniture' },
  { href: '/products/fashion-and-apparel', label: 'Browse fashion' },
  { href: '/sitemap.xml', label: 'Sitemap', static: true },
  { href: '/llms.txt', label: 'llms.txt', static: true },
  { href: '/agent.txt', label: 'Agent instructions', static: true },
]

/**
 * Plain-prose restatement of the machine-readable paths.
 *
 * Belt and braces for the markdown rendering of this page. `dupe.com` serves a
 * markdown variant on `Accept: text/markdown`, and that conversion runs the
 * HTML through article extraction — which routinely discards `<nav>` as
 * boilerplate. If that happens, the link list below vanishes from the markdown
 * and the 404 body goes back to saying nothing useful. A `<p>` of ordinary
 * sentence text is the one thing extraction reliably keeps, so the paths are
 * stated here as text as well as linked above. Cheap, and it costs a sighted
 * user only one short line.
 */
const MachineReadablePaths: React.FC = () => (
  <p className='pt-3 text-sm text-neutral-500 dark:text-neutral-500'>
    Machine-readable entry points: /llms.txt for a site overview, /agent.txt for
    agent instructions, /sitemap.xml for the full page list.
  </p>
)

export const NotFoundRecoveryLinks: React.FC = () => (
  <nav
    aria-label='Where to go instead'
    className='max-w-md pt-2 text-center text-base font-normal'
  >
    <p className='text-neutral-600 dark:text-neutral-400'>
      This page does not exist. Try one of these instead:
    </p>
    <ul className='mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2'>
      {RECOVERY_LINKS.map(({ href, label, static: isStatic }) => (
        <li key={href}>
          {isStatic ? (
            <a href={href} className='underline hover:no-underline'>
              {label}
            </a>
          ) : (
            <Link href={href} className='underline hover:no-underline'>
              {label}
            </Link>
          )}
        </li>
      ))}
    </ul>
    <MachineReadablePaths />
  </nav>
)

export default NotFoundRecoveryLinks
